/**
 * The "no signal" fallback row. Defined as its own typed constant (rather
 * than indexed off `HARDCODED_DEFAULTS`) so the type system can guarantee it
 * exists — TypeScript would otherwise widen indexed access on
 * `Record<string, …>` to `… | undefined`, forcing a dead-code `?? []`
 * fallback. Single source of truth for the "absolute last resort" list —
 * used by:
 *
 * - The implicit-message creator's outer `try/catch` (so a thrown error
 *   doesn't suppress the banner; we still emit a sensible recommendation).
 * - The React component's prop default (so Storybook stories and any other
 *   prop-less render path still show something coherent).
 *
 * `HARDCODED_DEFAULTS.na` below points at this same constant so the two
 * surfaces can never drift.
 *
 * TODO(ACTIVATION-2809): Confirm with PM whether "total cascade failure"
 * should surface the `na` defaults at all, or whether silently suppressing
 * the banner is the preferred UX. We picked "show na defaults" because the
 * cascade is designed never to legitimately produce `[]` — but the product
 * call deserves an explicit yes.
 */
export const DEFAULT_FALLBACK_APPS: ReadonlyArray<string> = ['google-drive', 'slack', 'github'];

/**
 * Hardcoded defaults per team-type key. Used as Tier 3 of the cascade when
 * the TAP traits don't yield enough apps.
 *
 * Data provenance: these defaults were derived by analysing third-party
 * smartlink usage **by team type across all Confluence tenants** — i.e. the
 * top apps each team type actually connects to in the wild. They are not
 * arbitrary picks; they represent the most common real-world usage today and
 * serve as a sensible "what would the average team in your role pick first?"
 * starting point when we have no per-user / per-tenant signal to personalise
 * with.
 *
 * Includes rows we can't currently reach from `TEAM_TYPE_TO_KEY` (`c_suite`,
 * `customer_service`, `data_science`) for completeness — they're here so the
 * table stays a faithful mirror of the upstream defaults dataset, and so
 * adding a new `TeamType` later doesn't require touching this map.
 *
 * Order within each row is significant: apps are appended to the cascade
 * result in this order, so the earlier an app appears, the more likely it
 * survives the `MAX_APPS` cap during dedupe.
 */
/* eslint-disable camelcase -- Keys are upstream TAP trait keys, which use snake_case by contract. */
export const HARDCODED_DEFAULTS: Record<string, ReadonlyArray<string>> = {
	c_suite: ['google-drive', 'slack', 'github'],
	customer_service: ['google-drive', 'slack', 'onedrive'],
	data_science: ['google-drive', 'github', 'slack'],
	design: ['google-drive', 'figma', 'slack'],
	engineering: ['google-drive', 'github', 'slack'],
	finance: ['google-drive', 'slack', 'onedrive'],
	human_resources: ['google-drive', 'slack', 'onedrive'],
	information_technology: ['google-drive', 'slack', 'github'],
	legal: ['google-drive', 'slack', 'sharepoint'],
	marketing: ['google-drive', 'slack', 'figma'],
	// `na` is the "no signal" floor row — kept in sync with the standalone
	// `DEFAULT_FALLBACK_APPS` export above by referencing the same constant.
	na: DEFAULT_FALLBACK_APPS,
	operations: ['google-drive', 'slack', 'onedrive'],
	other: ['google-drive', 'github', 'slack'],
	product_management: ['google-drive', 'figma', 'slack'],
	program_management: ['google-drive', 'onedrive', 'sharepoint'],
	sales: ['google-drive', 'slack', 'sharepoint'],
};
/* eslint-enable camelcase */
